<template>
    <h1 class="text-3xl font-extrabold tracking-tight text-center text-clay-800 md:text-4xl">
        Contact StageAgent
    </h1>

    <h2 class="mt-3 text-xl tracking-tight text-center font-display text-clay-950 sm:text-2xl">Get in touch and let us
        know how we can help.</h2>

    <div v-if="submitSuccessful" class="px-6 py-10 sm:px-10 lg:col-span-2 xl:p-12">
        <h3 class="text-lg font-medium text-center text-clay-800">Thank you! We'll get in touch with you soon.
        </h3>
    </div>
    <div v-else class="px-6 py-10 sm:px-10 lg:col-span-1 xl:p-12">
        <h3 class="text-xl font-semibold uppercase text-clay-800">Send us a message</h3>

        <form class="grid grid-cols-1 mt-6 gap-y-6">

            <ErrorSummary :response-status="responseStatus" class="mt-2"/>

            <div class="col-span-2">
                <label for="name" class="block text-sm font-medium text-clay-800">Name</label>
                <div class="mt-1">
                    <input v-model="request.name" type="text" name="name" id="name" autocomplete="given-name"
                           required
                           class="block w-full px-4 py-3 bg-white border rounded-md shadow-sm text-clay-800 focus:ring-electric-500 focus:border-electric-500 border-warm-gray-300 border-clay-500">
                </div>
            </div>

            <div class="col-span-2">
                <label for="email" class="block text-sm font-medium text-clay-800">Email</label>
                <div class="mt-1">
                    <input v-model="request.email" id="email" name="email" type="email" autocomplete="email" required
                           class="block w-full px-4 py-3 bg-white border rounded-md shadow-sm text-clay-800 focus:ring-electric-500 focus:border-electric-500 border-warm-gray-300 border-clay-500">
                </div>
            </div>
            <fieldset class="col-span-2">
                <legend class="block text-sm font-medium text-gray-700">
                    Subject
                </legend>
                <div class="grid grid-cols-1 mt-4 lg:grid-cols-2 gap-y-4">
                    <div v-for="subject in props.subjectOptions" class="flex items-center">
                        <input :id="subject" 
                               name="subject" 
                               :value="subject"
                               v-model="request.subject"
                               type="radio"
                               class="w-4 h-4 border-clay-500 focus:ring-grape-500 text-grape-600">
                        <label :for="subject" class="ml-3">
                            <span class="block text-sm font-semibold leading-6 text-gray-900">
                                {{subject}}
                            </span>
                        </label>
                    </div>

                </div>
            </fieldset>
            <div class="sm:col-span-2">
                <div class="flex justify-between">
                    <label for="message"
                           class="block text-sm font-medium text-clay-800">Message</label>
                </div>
                <div class="mt-1">
                    <textarea v-model="request.message" 
                              id="message" 
                              name="message" 
                              rows="4" 
                              required
                              class="block w-full px-4 py-3 bg-white border rounded-md shadow-sm text-clay-800 focus:ring-electric-500 focus:border-electric-500 border-warm-gray-300 border-clay-500"
                              aria-describedby="message-max">
                    </textarea>
                </div>
            </div>

            <div class="col-span-2">
                <div class="mx-auto mt-1 text-center">
                    <VueRecaptcha
                            class="inline-block mx-auto text-center g-recaptcha w-100 captcha-container contact-captcha"
                            ref="recaptcha"
                            :sitekey="siteKey"
                            :load-recaptcha-script="true"
                            @verify="handleSuccess"
                            @error="handleError"
                    ></VueRecaptcha>
                </div>
            </div>
            <div class="mt-10 text-center">
                <SaButton @click.prevent="submit" :loading="isSubmitting" variant="primary">
                    <template #loading>
                        <span class="font-semibold">Submitting...</span>
                    </template>
                    <span class="font-semibold">Submit</span>
                </SaButton>
            </div>
        </form>
    </div>

</template>

<script lang="ts" setup>
import {computed, defineProps, Ref, ref} from "vue";
import {client, trackEvent} from '@/shared';
import { VueRecaptcha } from 'vue-recaptcha';
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import {ContactUsRequest} from "@/dtos";
import SaButton from "@/main/components/SaButton.vue";

const props = defineProps<{
    subjectOptions: any []
}>();
const request = ref(new ContactUsRequest());

const responseStatus = ref(null);
const isSubmitting = ref(false);
const submitSuccessful: Ref<Boolean> = ref(false);

const submit = async () => {
    isSubmitting.value = true;
    request.value.gRecaptchaResponse = gRecaptchaResponse.value;
    
    await client.post<any>(request.value)
        .then(_ => {
            // only send subject to GA 
            trackEvent( 'contact_form_submitted', {
                subject: request.value.subject
            }, false, true);

            // send details to cio
            trackEvent( 'contact_form_submitted', request.value, true, false);
            
            submitSuccessful.value = true;
            responseStatus.value = null;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            recaptcha.value?.reset();
        });

    isSubmitting.value = false;
}

const recaptcha = ref<InstanceType<typeof VueRecaptcha> | null>(null);
const gRecaptchaResponse = ref('');

const siteKey = computed(() => {
    return '6Lf2aEMUAAAAAM-OcLydSmjTdZFjbztBs7p7tMHp';
});

const handleError = () => {
    // Do some validation
};

const handleSuccess = (response: string) => {
    gRecaptchaResponse.value = response;
};
</script>